import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Search,
  History,
  Warehouse,
  Package2,
  RefreshCcw,
  Pencil,
  Trash2,
  ArrowLeft,
  IndianRupee,
  Boxes,
  Layers3,
  X,
  Save,
  Eye,
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import { pricingService } from "../../../services/pricing.service";
import { warehouseService } from "../../../services/warehouse.service";
import ThemeMessagePopup from "./component/ThemeMessagePopup";
import { appConfirm } from "../../../utils/appPopup";

const PAGE_SIZE = 20;

function normalizeArray(input) {
  if (Array.isArray(input)) return input;
  if (Array.isArray(input?.items)) return input.items;
  if (Array.isArray(input?.data)) return input.data;
  if (Array.isArray(input?.rows)) return input.rows;
  return [];
}

function getTotalCount(input, fallbackLength = 0) {
  const raw =
    input?.total ??
    input?.count ??
    input?.totalCount ??
    input?.pagination?.total ??
    input?.meta?.total ??
    input?.data?.total;

  const n = Number(raw);
  return Number.isFinite(n) && n >= 0 ? n : fallbackLength;
}

function formatMoney(value, currency = "INR") {
  const num = Number(value);
  if (!Number.isFinite(num)) return "—";
  const symbol = currency === "INR" ? "₹" : `${currency} `;
  return `${symbol}${num.toFixed(2)}`;
}

function formatNumber(value) {
  const num = Number(value);
  if (!Number.isFinite(num)) return value ?? "—";
  return num.toLocaleString("en-IN", {
    maximumFractionDigits: 3,
  });
}

function formatDate(value) {
  if (!value) return "—";
  const dt = new Date(value);
  if (Number.isNaN(dt.getTime())) return "—";
  return dt.toLocaleDateString("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
}

function SummaryCard({ icon: Icon, title, value, subtext }) {
  return (
    <div className="rounded-[22px] border border-[#f3d6da] bg-white p-5 shadow-[0_10px_30px_rgba(216,106,120,0.08)]">
      <div className="flex items-start justify-between gap-3">
        <div>
          <p className="text-[13px] font-medium text-slate-500">{title}</p>
          <h3 className="mt-2 text-[26px] font-bold text-slate-800">
            {value}
          </h3>
          <p className="mt-1 text-[12px] text-slate-400">{subtext}</p>
        </div>
        <div className="flex h-12 w-12 items-center justify-center rounded-[16px] bg-[#fff0f3] text-[#d86a78]">
          <Icon size={22} />
        </div>
      </div>
    </div>
  );
}

function EditPurchaseHistoryModal({ open, item, loading, onClose, onSave }) {
  const [purchasePrice, setPurchasePrice] = useState("");
  const [purchaseCurrency, setPurchaseCurrency] = useState("INR");

  useEffect(() => {
    if (!open || !item) return;
    setPurchasePrice(
      item?.purchasePrice === null || item?.purchasePrice === undefined
        ? ""
        : String(item.purchasePrice)
    );
    setPurchaseCurrency(item?.purchaseCurrency || "INR");
  }, [open, item]);

  if (!open || !item) return null;

  return (
    <div className="fixed inset-0 z-[1400] flex items-center justify-center bg-black/35 p-4 backdrop-blur-[2px]">
      <div className="w-full max-w-[560px] rounded-[28px] border border-[#f0d3d8] bg-white shadow-[0_25px_90px_rgba(15,23,42,0.18)]">
        <div className="flex items-start justify-between gap-4 border-b border-[#f6e1e4] px-6 py-5">
          <div>
            <h3 className="text-[24px] font-bold text-slate-800">
              Edit Purchase History
            </h3>
            <p className="mt-1 text-[13px] text-slate-500">
              Update purchase price for selected batch record.
            </p>
          </div>

          <button
            type="button"
            onClick={onClose}
            className="inline-flex h-10 w-10 items-center justify-center rounded-full border border-[#f2d9dd] text-slate-500 transition hover:bg-[#fff3f5]"
          >
            <X size={18} />
          </button>
        </div>

        <div className="space-y-5 px-6 py-6">
          <div className="grid grid-cols-1 gap-4 rounded-[20px] bg-[#fff8f9] p-4 md:grid-cols-2">
            <div>
              <p className="text-[11px] font-semibold uppercase tracking-[0.08em] text-slate-400">
                Product
              </p>
              <p className="mt-1 text-[14px] font-bold text-slate-800">
                {item?.productName || "—"}
              </p>
            </div>

            <div>
              <p className="text-[11px] font-semibold uppercase tracking-[0.08em] text-slate-400">
                Batch
              </p>
              <p className="mt-1 text-[14px] font-bold text-slate-800">
                {item?.batchNo || "—"}
              </p>
            </div>

            <div>
              <p className="text-[11px] font-semibold uppercase tracking-[0.08em] text-slate-400">
                Warehouse
              </p>
              <p className="mt-1 text-[14px] font-bold text-slate-800">
                {item?.warehouseName || "—"}
              </p>
            </div>

            <div>
              <p className="text-[11px] font-semibold uppercase tracking-[0.08em] text-slate-400">
                Receipt
              </p>
              <p className="mt-1 text-[14px] font-bold text-slate-800">
                {item?.receiptCode || "—"}
              </p>
            </div>
          </div>

          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            <div>
              <label className="mb-2 block text-[13px] font-semibold text-slate-700">
                Purchase Price
              </label>
              <input
                type="number"
                min="0"
                step="0.01"
                value={purchasePrice}
                onChange={(e) => setPurchasePrice(e.target.value)}
                placeholder="Enter purchase price"
                className="h-12 w-full rounded-[16px] border border-[#edd6da] bg-[#fffdfd] px-4 text-[14px] outline-none focus:border-[#d86a78] focus:ring-2 focus:ring-[#f8dde1]"
              />
            </div>

            <div>
              <label className="mb-2 block text-[13px] font-semibold text-slate-700">
                Currency
              </label>
              <input
                value={purchaseCurrency}
                readOnly
                placeholder="INR"
                className="h-12 w-full rounded-[16px] border border-[#edd6da] bg-slate-50 px-4 text-[14px] uppercase text-slate-600 outline-none"
              />
            </div>
          </div>
        </div>

        <div className="flex flex-col-reverse gap-3 border-t border-[#f6e1e4] px-6 py-5 sm:flex-row sm:justify-end">
          <button
            type="button"
            onClick={onClose}
            className="inline-flex h-12 items-center justify-center rounded-[16px] border border-[#ecd7db] px-5 text-[14px] font-semibold text-slate-700 transition hover:bg-[#fff7f8]"
          >
            Cancel
          </button>

          <button
            type="button"
            disabled={loading}
            onClick={() =>
              onSave({
                purchasePrice,
                purchaseCurrency: "INR",
              })
            }
            className="inline-flex h-12 items-center justify-center gap-2 rounded-[16px] bg-[#d86a78] px-5 text-[14px] font-semibold text-white transition hover:bg-[#c95a69] disabled:cursor-not-allowed disabled:opacity-60"
          >
            <Save size={16} />
            {loading ? "Saving..." : "Save Changes"}
          </button>
        </div>
      </div>
    </div>
  );
}

function PurchaseDetailsModal({
  open,
  group,
  deletingId,
  onClose,
  onEdit,
  onDelete,
}) {
  if (!open || !group) return null;

  return (
    <div className="fixed inset-0 z-[1300] flex items-center justify-center bg-black/40 p-4 backdrop-blur-[2px]">
      <div className="flex max-h-[90vh] w-full max-w-[1120px] flex-col overflow-hidden rounded-[28px] border border-[#f2d9dd] bg-white shadow-[0_25px_90px_rgba(15,23,42,0.22)]">
        <div className="flex flex-col gap-4 border-b border-[#f3dbe0] px-6 py-5 md:flex-row md:items-start md:justify-between">
          <div>
            <div className="inline-flex items-center gap-2 rounded-full bg-[#fff0f3] px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.12em] text-[#d86a78]">
              <Eye size={13} />
              Batch Details
            </div>

            <h2 className="mt-3 text-[26px] font-bold text-slate-800">
              {group.productName}
            </h2>

            <p className="mt-1 text-[13px] text-slate-500">
              SKU: {group.sku || "—"} · Warehouse: {group.warehouseName || "—"}
            </p>
          </div>

          <button
            type="button"
            onClick={onClose}
            className="inline-flex h-10 w-10 items-center justify-center rounded-full border border-[#edd6da] text-slate-500 transition hover:bg-[#fff7f8]"
          >
            <X size={18} />
          </button>
        </div>

        <div className="grid grid-cols-1 gap-4 border-b border-[#f3dbe0] bg-[#fff8f9] px-6 py-5 md:grid-cols-4">
          <div className="rounded-[18px] border border-[#f3dbe0] bg-white p-4">
            <p className="text-[12px] font-semibold text-slate-500">Batches</p>
            <p className="mt-1 text-[22px] font-bold text-slate-800">
              {group.batches.length}
            </p>
          </div>

          <div className="rounded-[18px] border border-[#f3dbe0] bg-white p-4">
            <p className="text-[12px] font-semibold text-slate-500">
              Received Qty
            </p>
            <p className="mt-1 text-[22px] font-bold text-slate-800">
              {formatNumber(group.totalReceived)}
            </p>
          </div>

          <div className="rounded-[18px] border border-[#f3dbe0] bg-white p-4">
            <p className="text-[12px] font-semibold text-slate-500">
              Available Qty
            </p>
            <p className="mt-1 text-[22px] font-bold text-slate-800">
              {formatNumber(group.totalAvailable)}
            </p>
          </div>

          <div className="rounded-[18px] border border-[#f3dbe0] bg-white p-4">
            <p className="text-[12px] font-semibold text-slate-500">
              Subtotal
            </p>
            <p className="mt-1 text-[22px] font-bold text-slate-800">
              {formatMoney(group.subtotal, group.currency)}
            </p>
          </div>
        </div>

        <div className="flex-1 overflow-y-auto p-6">
          <div className="overflow-hidden rounded-[22px] border border-[#f2d9dd]">
            <div className="overflow-x-auto">
              <table className="w-full min-w-[980px]">
                <thead className="bg-[#fff7f8]">
                  <tr className="text-left text-[12px] font-semibold uppercase tracking-[0.08em] text-slate-500">
                    <th className="px-4 py-4">Batch</th>
                    <th className="px-4 py-4">Purchase Price</th>
                    <th className="px-4 py-4">Received Qty</th>
                    <th className="px-4 py-4">Available Qty</th>
                    <th className="px-4 py-4">Receipt</th>
                    <th className="px-4 py-4">Dates</th>
                    <th className="px-4 py-4 text-right">Actions</th>
                  </tr>
                </thead>

                <tbody>
                  {group.batches.map((item) => (
                    <tr key={item?.id} className="border-t border-[#f5e1e4]">
                      <td className="px-4 py-4">
                        <div className="inline-flex rounded-full bg-[#fff1f3] px-3 py-1 text-[12px] font-semibold text-[#d86a78]">
                          {item?.batchNo || "—"}
                        </div>
                        <p className="mt-2 text-[12px] text-slate-500">
                          UOM: {item?.uom || "—"}
                        </p>
                      </td>

                      <td className="px-4 py-4">
                        <p className="text-[14px] font-bold text-slate-800">
                          {formatMoney(
                            item?.purchasePrice,
                            item?.purchaseCurrency || "INR"
                          )}
                        </p>
                        <p className="mt-1 text-[12px] text-slate-500">
                          Currency: {item?.purchaseCurrency || "INR"}
                        </p>
                      </td>

                      <td className="px-4 py-4 text-[14px] font-semibold text-slate-700">
                        {formatNumber(item?.qtyReceived)}
                      </td>

                      <td className="px-4 py-4 text-[14px] font-semibold text-slate-700">
                        {formatNumber(item?.qtyAvailable)}
                      </td>

                      <td className="px-4 py-4">
                        <p className="font-semibold text-slate-800">
                          {item?.receiptCode || "—"}
                        </p>
                        <p className="mt-1 text-[12px] text-slate-500">
                          Source: {item?.sourceType || "—"}
                        </p>
                      </td>

                      <td className="px-4 py-4">
                        <p className="text-[12px] text-slate-500">
                          Created:{" "}
                          <span className="font-medium text-slate-700">
                            {formatDate(item?.createdAt)}
                          </span>
                        </p>
                        <p className="mt-2 text-[12px] text-slate-500">
                          Updated:{" "}
                          <span className="font-medium text-slate-700">
                            {formatDate(item?.updatedAt)}
                          </span>
                        </p>
                        <p className="mt-2 text-[12px] text-slate-500">
                          Expiry:{" "}
                          <span className="font-medium text-slate-700">
                            {formatDate(item?.expiryDate)}
                          </span>
                        </p>
                      </td>

                      <td className="px-4 py-4">
                        <div className="flex justify-end gap-2">
                          <button
                            type="button"
                            onClick={() => onEdit(item)}
                            className="inline-flex h-10 items-center justify-center gap-2 rounded-[12px] border border-[#edd6da] px-4 text-[13px] font-semibold text-slate-700 transition hover:bg-[#fff7f8]"
                          >
                            <Pencil size={14} />
                            Edit
                          </button>

                          <button
                            type="button"
                            disabled={deletingId === String(item?.id)}
                            onClick={() => onDelete(item)}
                            className="inline-flex h-10 items-center justify-center gap-2 rounded-[12px] bg-rose-50 px-4 text-[13px] font-semibold text-rose-600 transition hover:bg-rose-100 disabled:cursor-not-allowed disabled:opacity-60"
                          >
                            <Trash2 size={14} />
                            {deletingId === String(item?.id)
                              ? "Clearing..."
                              : "Clear"}
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default function PurchaseHistoryPage() {
  const navigate = useNavigate();

  const [items, setItems] = useState([]);
  const [warehouses, setWarehouses] = useState([]);
  const [warehouseId, setWarehouseId] = useState("");
  const [searchText, setSearchText] = useState("");
  const [query, setQuery] = useState("");
  const [batchNo, setBatchNo] = useState("");
  const [page, setPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);

  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [selectedGroup, setSelectedGroup] = useState(null);
  const [submitLoading, setSubmitLoading] = useState(false);
  const [deletingId, setDeletingId] = useState("");

  const [popup, setPopup] = useState({
    open: false,
    type: "info",
    title: "",
    message: "",
  });

  const totalPages = Math.max(1, Math.ceil((totalCount || 0) / PAGE_SIZE));

  const openPopup = useCallback((type, title, message) => {
    setPopup({ open: true, type, title, message });
  }, []);

  const loadWarehouses = useCallback(async () => {
    try {
      const res = await warehouseService.listWarehouses({
        page: 1,
        limit: 200,
      });
      setWarehouses(normalizeArray(res));
    } catch (error) {
      openPopup(
        "error",
        "Warehouse load failed",
        error?.message || "Unable to fetch warehouse list."
      );
    }
  }, [openPopup]);

  const loadHistory = useCallback(
    async ({ silent = false } = {}) => {
      try {
        if (silent) setRefreshing(true);
        else setLoading(true);

        const res = await pricingService.listPurchaseHistory({
          warehouseId,
          batchNo,
          q: query,
          page,
          limit: PAGE_SIZE,
        });

        const list = normalizeArray(res);
        setItems(list);
        setTotalCount(getTotalCount(res, list.length));
      } catch (error) {
        setItems([]);
        setTotalCount(0);
        openPopup(
          "error",
          "History load failed",
          error?.message || "Unable to fetch purchase history."
        );
      } finally {
        setLoading(false);
        setRefreshing(false);
      }
    },
    [warehouseId, batchNo, query, page, openPopup]
  );

  useEffect(() => {
    loadWarehouses();
  }, [loadWarehouses]);

  useEffect(() => {
    loadHistory();
  }, [loadHistory]);

  useEffect(() => {
    if (!popup.open) return undefined;
    const timer = window.setTimeout(() => {
      setPopup((prev) => ({ ...prev, open: false }));
    }, 3200);
    return () => window.clearTimeout(timer);
  }, [popup.open]);

  const selectedWarehouseName = useMemo(() => {
    const found = warehouses.find((w) => String(w?.id) === String(warehouseId));
    return found?.name || found?.warehouse_name || "";
  }, [warehouses, warehouseId]);

  const groupedRows = useMemo(() => {
    const map = new Map();

    items.forEach((item) => {
      const key = `${item?.warehouseId || "no-wh"}-${item?.productId || item?.sku || item?.productName || "product"
        }`;

      if (!map.has(key)) {
        map.set(key, {
          key,
          productId: item?.productId || "",
          productName: item?.productName || "—",
          sku: item?.sku || "—",
          barcode: item?.barcode || "—",
          warehouseName: item?.warehouseName || "—",
          warehouseId: item?.warehouseId || "",
          currency: item?.purchaseCurrency || "INR",
          batches: [],
          totalReceived: 0,
          totalAvailable: 0,
          subtotal: 0,
        });
      }

      const row = map.get(key);
      const price = Number(item?.purchasePrice || 0);
      const received = Number(item?.qtyReceived || 0);
      const available = Number(item?.qtyAvailable || 0);

      row.batches.push(item);
      row.totalReceived += Number.isFinite(received) ? received : 0;
      row.totalAvailable += Number.isFinite(available) ? available : 0;
      row.subtotal += Number.isFinite(price * received) ? price * received : 0;
    });

    return Array.from(map.values());
  }, [items]);

  const stats = useMemo(() => {
    const uniqueProducts = new Set(
      items.map((item) => String(item?.productId || item?.sku || ""))
    ).size;

    const uniqueWarehouses = new Set(
      items.map((item) => String(item?.warehouseId || ""))
    ).size;

    const totalPurchase = items.reduce((sum, item) => {
      const price = Number(item?.purchasePrice || 0);
      const qty = Number(item?.qtyReceived || 0);
      return sum + (Number.isFinite(price) && Number.isFinite(qty) ? price * qty : 0);
    }, 0);

    return {
      uniqueProducts,
      uniqueWarehouses,
      totalPurchase,
    };
  }, [items]);

  const onSearchSubmit = (e) => {
    e?.preventDefault?.();
    setPage(1);
    setQuery(searchText.trim());
  };

  const onRefresh = () => {
    loadHistory({ silent: true });
  };

  const openGroupDetails = (group) => {
    setSelectedGroup(group);
    setDetailsOpen(true);
  };

  const closeGroupDetails = () => {
    setDetailsOpen(false);
    setSelectedGroup(null);
  };

  const handleOpenEdit = (item) => {
    setSelectedItem(item);
    setEditOpen(true);
  };

  const handleSaveEdit = async ({ purchasePrice, purchaseCurrency }) => {
    try {
      if (!selectedItem?.id) return;

      if (purchasePrice === "" || Number(purchasePrice) < 0) {
        openPopup("error", "Invalid price", "Please enter a valid purchase price.");
        return;
      }

      setSubmitLoading(true);

      await pricingService.updatePurchaseHistory(selectedItem.id, {
        purchasePrice: Number(purchasePrice),
        purchaseCurrency: purchaseCurrency || "INR",
      });

      openPopup(
        "success",
        "History updated",
        "Purchase history record updated successfully."
      );

      setEditOpen(false);
      setSelectedItem(null);
      loadHistory({ silent: true });
    } catch (error) {
      openPopup(
        "error",
        "Update failed",
        error?.message || "Unable to update purchase history."
      );
    } finally {
      setSubmitLoading(false);
    }
  };

  const handleDelete = async (item) => {
    if (!item?.id) return;

    const ok = await appConfirm(
      `Clear purchase price for batch ${item?.batchNo || "this record"}?`,
      { danger: true, confirmText: "CLEAR" }
    );
    if (!ok) return;

    try {
      setDeletingId(String(item.id));
      await pricingService.deletePurchaseHistory(item.id);

      openPopup(
        "success",
        "History cleared",
        "Purchase price has been cleared successfully."
      );

      await loadHistory({ silent: true });
    } catch (error) {
      openPopup(
        "error",
        "Delete failed",
        error?.message || "Unable to clear purchase history."
      );
    } finally {
      setDeletingId("");
    }
  };

  return (
    <div className="min-h-screen bg-[#fff8f9] px-4 py-5 sm:px-6 lg:px-8">
      <div className="mx-auto max-w-[1650px] space-y-6">
        <div className="overflow-hidden rounded-[28px] border border-[#f2d9dd] bg-[linear-gradient(135deg,#fff7f8_0%,#ffffff_55%,#fff5f7_100%)] shadow-[0_18px_50px_rgba(216,106,120,0.10)]">
          <div className="flex flex-col gap-6 px-6 py-6 lg:flex-row lg:items-center lg:justify-between lg:px-8">
            <div>
              <div className="inline-flex items-center gap-2 rounded-full bg-[#fff0f3] px-4 py-2 text-[12px] font-semibold uppercase tracking-[0.12em] text-[#d86a78]">
                <History size={14} />
                Purchase Price History
              </div>

              <h1 className="mt-4 text-[32px] font-bold tracking-tight text-slate-900">
                Batch purchase history
              </h1>

              <p className="mt-2 max-w-[820px] text-[14px] leading-6 text-slate-500">
                Grouped by product and warehouse. Click any row to view all
                batch records, purchase prices, quantities and receipts.
              </p>
            </div>

            <div className="flex flex-wrap items-center gap-3">
              <button
                type="button"
                onClick={() => navigate("/admin/dashboard/inventory")}
                className="inline-flex h-12 items-center justify-center gap-2 rounded-[16px] border border-[#edd6da] bg-white px-5 text-[14px] font-semibold text-slate-700 transition hover:bg-[#fff7f8]"
              >
                <ArrowLeft size={16} />
                Back to Inventory
              </button>

              <button
                type="button"
                onClick={onRefresh}
                className="inline-flex h-12 items-center justify-center gap-2 rounded-[16px] bg-[#d86a78] px-5 text-[14px] font-semibold text-white transition hover:bg-[#c95a69]"
              >
                <RefreshCcw
                  size={16}
                  className={refreshing ? "animate-spin" : ""}
                />
                Refresh
              </button>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 gap-4 xl:grid-cols-4">
          <SummaryCard
            icon={Layers3}
            title="Visible products"
            value={groupedRows.length}
            subtext="Grouped product rows for current filters"
          />

          <SummaryCard
            icon={Package2}
            title="Products covered"
            value={stats.uniqueProducts}
            subtext="Unique products on this page"
          />

          <SummaryCard
            icon={Warehouse}
            title="Warehouses covered"
            value={stats.uniqueWarehouses}
            subtext="Unique warehouses on this page"
          />

          <SummaryCard
            icon={IndianRupee}
            title="Visible purchase value"
            value={formatMoney(stats.totalPurchase)}
            subtext="Purchase price × received quantity"
          />
        </div>

        <div className="rounded-[24px] border border-[#f2d9dd] bg-white p-5 shadow-sm">
          <div className="grid grid-cols-1 gap-4 xl:grid-cols-[1.1fr_0.8fr_0.7fr_auto]">
            <form onSubmit={onSearchSubmit} className="relative">
              <label className="mb-2 block text-[13px] font-semibold text-slate-700">
                Search
              </label>

              <Search
                size={18}
                className="pointer-events-none absolute left-4 top-[46px] text-slate-400"
              />

              <input
                value={searchText}
                onChange={(e) => setSearchText(e.target.value)}
                placeholder="Search by product, receipt, SKU, warehouse"
                className="h-12 w-full rounded-[16px] border border-[#edd6da] bg-[#fffdfd] pl-11 pr-4 text-[14px] outline-none focus:border-[#d86a78] focus:ring-2 focus:ring-[#f8dde1]"
              />
            </form>

            <div>
              <label className="mb-2 block text-[13px] font-semibold text-slate-700">
                Warehouse
              </label>

              <select
                value={warehouseId}
                onChange={(e) => {
                  setWarehouseId(e.target.value);
                  setPage(1);
                }}
                className="h-12 w-full rounded-[16px] border border-[#edd6da] bg-[#fffdfd] px-4 text-[14px] outline-none focus:border-[#d86a78] focus:ring-2 focus:ring-[#f8dde1]"
              >
                <option value="">All warehouses</option>
                {warehouses.map((item) => (
                  <option key={item?.id} value={item?.id}>
                    {item?.name || item?.warehouse_name || "Warehouse"}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="mb-2 block text-[13px] font-semibold text-slate-700">
                Batch No
              </label>

              <input
                value={batchNo}
                onChange={(e) => {
                  setBatchNo(e.target.value);
                  setPage(1);
                }}
                placeholder="Search batch no"
                className="h-12 w-full rounded-[16px] border border-[#edd6da] bg-[#fffdfd] px-4 text-[14px] outline-none focus:border-[#d86a78] focus:ring-2 focus:ring-[#f8dde1]"
              />
            </div>

            <div className="flex gap-3 xl:justify-end xl:pt-[29px]">
              <button
                type="button"
                onClick={onSearchSubmit}
                className="inline-flex h-12 items-center justify-center gap-2 rounded-[16px] bg-[#d86a78] px-5 text-[14px] font-semibold text-white transition hover:bg-[#c95a69]"
              >
                <Search size={16} />
                Search
              </button>
            </div>
          </div>

          <div className="mt-4 flex flex-wrap items-center gap-2">
            <span className="rounded-full bg-[#fff1f3] px-3 py-1 text-[12px] font-semibold text-[#d86a78]">
              {selectedWarehouseName
                ? `Warehouse: ${selectedWarehouseName}`
                : "All warehouses"}
            </span>

            {query ? (
              <span className="rounded-full bg-slate-100 px-3 py-1 text-[12px] font-semibold text-slate-600">
                Search: {query}
              </span>
            ) : null}

            {batchNo ? (
              <span className="rounded-full bg-slate-100 px-3 py-1 text-[12px] font-semibold text-slate-600">
                Batch: {batchNo}
              </span>
            ) : null}
          </div>
        </div>

        <div className="overflow-hidden rounded-[24px] border border-[#f2d9dd] bg-white shadow-sm">
          <div className="overflow-x-auto">
            <table className="w-full min-w-[1050px]">
              <thead className="bg-[#fff7f8]">
                <tr className="text-left text-[12px] font-semibold uppercase tracking-[0.08em] text-slate-500">
                  <th className="px-5 py-4">Product</th>
                  <th className="px-5 py-4">Warehouse</th>
                  <th className="px-5 py-4">Batches</th>
                  <th className="px-5 py-4">Received Qty</th>
                  <th className="px-5 py-4">Available Qty</th>
                  <th className="px-5 py-4">Subtotal</th>
                  <th className="px-5 py-4 text-right">Action</th>
                </tr>
              </thead>

              <tbody>
                {loading ? (
                  Array.from({ length: 8 }).map((_, index) => (
                    <tr key={index} className="border-t border-[#f5e1e4]">
                      <td colSpan={7} className="px-5 py-4">
                        <div className="h-12 animate-pulse rounded-[14px] bg-[#fff2f4]" />
                      </td>
                    </tr>
                  ))
                ) : groupedRows.length ? (
                  groupedRows.map((group) => (
                    <tr
                      key={group.key}
                      onClick={() => openGroupDetails(group)}
                      className="cursor-pointer border-t border-[#f5e1e4] transition hover:bg-[#fff7f8]"
                    >
                      <td className="px-5 py-4">
                        <div className="font-bold text-slate-800">
                          {group.productName}
                        </div>
                        <div className="mt-1 text-[12px] text-slate-500">
                          SKU: {group.sku || "—"}
                        </div>
                        <div className="mt-1 text-[12px] text-slate-500">
                          Barcode: {group.barcode || "—"}
                        </div>
                      </td>

                      <td className="px-5 py-4">
                        <div className="font-semibold text-slate-800">
                          {group.warehouseName}
                        </div>
                      </td>

                      <td className="px-5 py-4">
                        <span className="inline-flex rounded-full bg-[#fff1f3] px-3 py-1 text-[12px] font-semibold text-[#d86a78]">
                          {group.batches.length} batches
                        </span>
                      </td>

                      <td className="px-5 py-4 text-[14px] font-semibold text-slate-700">
                        {formatNumber(group.totalReceived)}
                      </td>

                      <td className="px-5 py-4 text-[14px] font-semibold text-slate-700">
                        {formatNumber(group.totalAvailable)}
                      </td>

                      <td className="px-5 py-4 text-[15px] font-bold text-slate-800">
                        {formatMoney(group.subtotal, group.currency)}
                      </td>

                      <td className="px-5 py-4">
                        <div className="flex justify-end">
                          <button
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              openGroupDetails(group);
                            }}
                            className="inline-flex h-10 items-center justify-center gap-2 rounded-[12px] border border-[#edd6da] px-4 text-[13px] font-semibold text-slate-700 transition hover:bg-[#fff7f8]"
                          >
                            <Eye size={14} />
                            View
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan={7} className="px-5 py-14 text-center">
                      <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-[#fff0f3] text-[#d86a78]">
                        <Boxes size={28} />
                      </div>

                      <h3 className="mt-4 text-[22px] font-bold text-slate-800">
                        No purchase history found
                      </h3>

                      <p className="mx-auto mt-2 max-w-[560px] text-[14px] leading-6 text-slate-500">
                        Try another warehouse, batch number, or search query.
                      </p>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          <div className="flex flex-col gap-3 border-t border-[#f5e1e4] px-5 py-4 sm:flex-row sm:items-center sm:justify-between">
            <p className="text-[13px] text-slate-500">
              Showing page{" "}
              <span className="font-semibold text-slate-700">{page}</span> of{" "}
              <span className="font-semibold text-slate-700">
                {totalPages}
              </span>
            </p>

            <div className="flex gap-2">
              <button
                type="button"
                disabled={page <= 1}
                onClick={() => setPage((prev) => Math.max(1, prev - 1))}
                className="inline-flex h-11 items-center justify-center rounded-[14px] border border-[#edd6da] px-4 text-[13px] font-semibold text-slate-700 transition hover:bg-[#fff7f8] disabled:cursor-not-allowed disabled:opacity-50"
              >
                Previous
              </button>

              <button
                type="button"
                disabled={page >= totalPages}
                onClick={() => setPage((prev) => prev + 1)}
                className="inline-flex h-11 items-center justify-center rounded-[14px] border border-[#edd6da] px-4 text-[13px] font-semibold text-slate-700 transition hover:bg-[#fff7f8] disabled:cursor-not-allowed disabled:opacity-50"
              >
                Next
              </button>
            </div>
          </div>
        </div>
      </div>

      <PurchaseDetailsModal
        open={detailsOpen}
        group={selectedGroup}
        deletingId={deletingId}
        onClose={closeGroupDetails}
        onEdit={handleOpenEdit}
        onDelete={handleDelete}
      />

      <EditPurchaseHistoryModal
        open={editOpen}
        item={selectedItem}
        loading={submitLoading}
        onClose={() => {
          if (submitLoading) return;
          setEditOpen(false);
          setSelectedItem(null);
        }}
        onSave={handleSaveEdit}
      />

      <ThemeMessagePopup
        open={popup.open}
        type={popup.type}
        title={popup.title}
        message={popup.message}
        onClose={() => setPopup((prev) => ({ ...prev, open: false }))}
      />
    </div>
  );
}